import { ComponentCollection } from '../ComponentCollection'
import { Application } from './Application'

/**
 * Class ApplicationCollection
 *
 * @extends ComponentCollection
 */
export class ApplicationCollection extends ComponentCollection {
  constructor(data, userContext = null) {
    super()
    this.userGroupValue = userContext?.groupValue ?? null
    this.userCountryCode = userContext?.countryCode ?? null
    this.setItems(data)
  }

  setUserContext(groupValue, countryCode) {
    this.userGroupValue = groupValue
    this.userCountryCode = countryCode
    this.applyRestrictions()
  }

  setItems(data) {
    this.items = data.map((a) => new Application(a))
    this.applyRestrictions()
    this.initDependencies()
  }

  applyRestrictions() {
    if (this.userGroupValue === null) {
      return
    }

    this.items.forEach((app) => {
      app.checkRestrictions(this.userGroupValue, this.userCountryCode)
    })
  }

  getEnabledApplications() {
    return this.items.filter((a) => a.isSelected)
  }

  initDependencies() {
    this.items.forEach((app) => {
      if (app.isSelected) {
        this.handleDependencies(app)
      }
    })
  }

  /**
   *
   * @param {Application} app
   * @param {boolean} skipReEvaluation - Internal flag to skip re-evaluation during recursion
   */
  handleDependencies(app, skipReEvaluation = false) {
    if (!app || !app.dependencies || app.dependencies.length === 0) {
      return
    }

    app.dependencies.forEach((d) => {
      if (!d || !d.dependentOnAppId) {
        return
      }

      let found = this.items.find((a) => a.appId == d.dependentOnAppId)

      if (!found) {
        return
      }

      // Only handle dependencies when enabling (cascade enablee)
      if (app.value) {
        // Set the dependency's value to enabled
        found.value = app.value

        // Recursively handle dependencies of the dependency (cascade enable)
        if (found.dependencies && found.dependencies.length > 0) {
          this.handleDependencies(found, true)
        }
      }
      // When removing an app, we don't automatically deactivate dependencies
      // They will just become non-readOnly so the user can manually deactivate them if desired
    })

    // Only re-evaluate readOnly state once at the top level, not during recursion
    if (!skipReEvaluation) {
      this.reEvaluateReadOnlyState()
    }
  }

  reEvaluateReadOnlyState() {
    this.items.forEach((app) => {
      app.readOnly = false
    })

    // For each enabled app, check its dependencies and mark them as readOnly
    // A dependency is readOnly if ANY enabled app depends on it
    this.getEnabledApplications().forEach((app) => {
      if (app.dependencies && app.dependencies.length) {
        app.dependencies.forEach((d) => {
          const found = this.items.find((a) => a.appId == d.dependentOnAppId)
          if (found && found.value) {
            // Mark as readOnly if this dependency is needed by any enabled app
            found.readOnly = true
          }
        })
      }
    })
  }

  hasRestrictedApplications() {
    return this.items.some((app) => app.isRestrictedByPermission)
  }
}
